import ReactGA from 'react-ga4';
import Clarity from '@microsoft/clarity';

const TRACKING_ID = 'G-PY2XCR8CNL';
const CLARITY_PROJECT_ID = 'thhhaan1d1';

// Initialize Google Analytics
ReactGA.initialize(TRACKING_ID);

// Initialize Microsoft Clarity
Clarity.init(CLARITY_PROJECT_ID);

console.log('Analytics initialized - GA4:', TRACKING_ID, 'Clarity:', CLARITY_PROJECT_ID);

// Enhanced page view tracking for both GA4 and Clarity
export const trackPageView = (page) => {
  // Google Analytics 4 pageview
  ReactGA.send({ 
    hitType: "pageview", 
    page: page,
    title: document.title 
  });
  
  // Microsoft Clarity page tracking
  Clarity.setTag('page_path', page);
  Clarity.event('page_view');
  
  console.log('Page view tracked:', page);
};

// Enhanced event tracking for both GA4 and Clarity
export const trackEvent = (eventName, parameters = {}) => {
  // Google Analytics 4 event
  ReactGA.event(eventName, parameters);
  
  // Microsoft Clarity custom event
  Clarity.event(eventName);
  
  console.log('Event tracked:', eventName, parameters);
};

// Track user interactions
export const trackUserInteraction = (elementType, elementId, action) => {
  const eventData = {
    element_type: elementType,
    element_id: elementId,
    action: action
  };
  
  // Google Analytics 4 interaction event
  ReactGA.event('user_interaction', eventData);
  
  // Microsoft Clarity custom event for user interactions
  Clarity.event(`interaction_${elementType}_${action}`);
  Clarity.setTag('interaction_element', `${elementType}_${elementId}`);
  
  console.log('User interaction tracked:', eventData);
};

// Form submission tracking
export const trackFormSubmission = (formName, formData = {}) => {
  // Google Analytics 4 conversion event
  ReactGA.event('form_submit', {
    form_name: formName,
    ...formData
  });
  
  // Microsoft Clarity custom event for form submission
  Clarity.event(`form_submit_${formName}`);
  Clarity.setTag('form_name', formName);
  
  console.log('Form submission tracked:', formName, formData);
};

// File upload tracking
export const trackFileUpload = (fileName, fileType, fileSize = null) => {
  const eventData = {
    file_name: fileName,
    file_type: fileType,
    file_size: fileSize
  };
  
  // Google Analytics 4 file upload event
  ReactGA.event('file_upload', eventData);
  
  // Microsoft Clarity custom event for file upload
  Clarity.event('file_upload');
  Clarity.setTag('upload_file_name', fileName);
  Clarity.setTag('upload_file_type', fileType);
  
  console.log('File upload tracked:', eventData);
};

// User identification (for authenticated users)
export const identifyUser = (userId, userProperties = {}) => {
  // Google Analytics 4 user identification
  ReactGA.set({ user_id: userId });
  ReactGA.set(userProperties);
  
  // Microsoft Clarity user identification
  Clarity.identify(userId, null, null, userProperties.name || '');
  
  // Set additional user properties in Clarity
  Object.keys(userProperties).forEach(key => {
    Clarity.setTag(`user_${key}`, userProperties[key]);
  });
  
  console.log('User identified:', userId, userProperties);
};

// Download tracking
export const trackDownload = (fileName, downloadUrl) => {
  const eventData = {
    file_name: fileName,
    link_url: downloadUrl
  };
  
  // Google Analytics 4 file download event
  ReactGA.event('file_download', eventData);
  
  // Microsoft Clarity custom event for downloads
  Clarity.event('file_download');
  Clarity.setTag('download_file', fileName);
  Clarity.setTag('download_url', downloadUrl);
  
  console.log('Download tracked:', eventData);
};

// External link tracking
export const trackExternalLink = (linkUrl, linkText = '') => {
  const eventData = {
    link_url: linkUrl,
    link_text: linkText
  };
  
  // Google Analytics 4 external link event
  ReactGA.event('click', {
    link_domain: new URL(linkUrl).hostname,
    ...eventData
  });
  
  // Microsoft Clarity custom event for external links
  Clarity.event('external_link_click');
  Clarity.setTag('external_link_url', linkUrl);
  Clarity.setTag('external_link_text', linkText);
  
  console.log('External link tracked:', eventData);
};

// Custom business events
export const trackBusinessEvent = (eventName, eventData = {}) => {
  // Google Analytics 4 custom event
  ReactGA.event(eventName, eventData);
  
  // Microsoft Clarity custom event
  Clarity.event(eventName);
  
  // Set event data as tags in Clarity
  Object.keys(eventData).forEach(key => {
    Clarity.setTag(`${eventName}_${key}`, eventData[key]);
  });
  
  console.log('Business event tracked:', eventName, eventData);
};

// Clarity-specific helper functions
export const clarityConsent = (consent = true) => {
  Clarity.consent(consent);
  console.log('Clarity consent set:', consent);
};

export const clarityUpgrade = (reason) => {
  Clarity.upgrade(reason);
  console.log('Clarity upgrade triggered:', reason);
};

// Export the Clarity instance for advanced usage
export { Clarity };
